import React, { useContext, Fragment, useMemo } from 'react'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import styled, { ThemeContext } from 'styled-components'
import { lighten } from 'polished'
import { useDispatch, useSelector } from 'react-redux'

import dayjs from 'dayjs'
import cn from 'classnames'
import upperFirst from 'lodash/upperFirst'
import upperCase from 'lodash/upperCase'
import { Tooltipped } from 'react-md'

import queryString from 'qs'
import flowRight from 'lodash/flowRight'

import TextField, { FieldLabel } from 'shared/components/TextField'
import MoreOptions from 'shared/components/DataTable/MoreOptions'
import Button from 'shared/components/Button'
import Avatar from 'pages/Parent/components/Avatar'
import Box from 'shared/components/Box'
import Loader from 'shared/components/Loader'

import SvgSendIcon from 'assets/icons/Send'
import SvgLocationIcon from 'assets/icons/Location'
import SvgCalendarIcon from 'assets/icons/Calendar'
import SvgGenderIcon from 'assets/icons/Gender'
import SvgCoachIcon from 'assets/icons/Coaches'
import SvgTimeIcon from 'assets/icons/Time'
import SvgAgeIcon from 'assets/icons/Age'
import SvgPencilIcon from 'assets/icons/EditInformation'
import SvgArchiveIcon from 'assets/icons/Archive'
import SvgVisibilityIcon from 'assets/icons/Visibility'

import { showDialog, hideDialog } from 'shared/redux/app/reducer'
import { UserRoleTypeWithStatus } from 'pages/UserRole/model/role'
import commentValidator, {
  CommentType,
  CommentTypeWithStatus,
  getEmptyComment,
} from 'shared/components/Notes/models/comment'
import { NoteType, NoteTypeWithStatus, getEmptyNote } from 'shared/components/Notes/models/note'
import { NoteCardHeader } from 'shared/components/Notes'
import { roleSelector, authSelector } from 'shared/hocs/withAuth'

import useForm from 'shared/hooks/useForm'
import useQuery from 'shared/hooks/useQuery'
import useMutation from 'shared/hooks/useMutation'

type Props = WrappedProps<NoteTypeWithStatus> & {
  isEdit?: boolean
  allowRoleVisibility?: boolean
  isEvent?: boolean
  note_id?: string
  refetch?: () => void
  onEdit: (data: NoteTypeWithStatus) => void
  onRestore: (data: NoteTypeWithStatus) => void
  onArchive: (data: NoteTypeWithStatus) => void
  onDelete: (data: NoteTypeWithStatus) => void
  onDeleteComment: (data: CommentType) => void
  onEditComment: (data: CommentType) => void
  onPreviewAttachment: (data: NoteType) => React.MouseEventHandler<HTMLDivElement>
}

const roleQueryString = queryString.stringify({
  communication: true,
  status: 'Active',
  fields: ['id', 'name', 'level'],
  sort: [{ column: 'level', direction: 'desc' }],
})

function PreviewNoteDialog(props: Props) {
  const {
    formState,
    formHandlers,
    allowRoleVisibility = false,
    isEvent = false,
    isEdit = false,
    note_id,
    onClose,
    onEdit,
    onRestore,
    onArchive,
    onDelete,
    onDeleteComment,
    onEditComment,
    onPreviewAttachment,
    refetch: refetchPageTable,
  } = props

  const { fields, errors } = formState
  const { onElementChange, onChange, onSetForm } = formHandlers
  const theme = useContext(ThemeContext)
  const { auth: user } = useSelector(authSelector)
  const [commentFormState, commentFormHandlers] = useForm<CommentType>({
    onValid: onSubmitComment,
    initialFields: {
      ...getEmptyComment(),
      note_id: note_id || fields?.id,
    },
  })

  const [noteQueryState] = useQuery<NoteType>(
    { url: `/note_report/${note_id}` },
    {
      isBase: true,
      initialData: getEmptyNote(),
      skip: !note_id,
      onFetchSuccess,
    },
  )

  const [noteCommentQueryState, noteCommentHandler] = useQuery<CommentTypeWithStatus[]>(
    { url: `/note_comment_report?note_id=${note_id || fields?.id}` },
    {
      isBase: true,
      initialData: [],
    },
  )

  const [commentState, onCommentMutation] = useMutation({
    url: 'note/comment',
    method: 'POST',
  })

  const shouldDisableCommentField = useMemo(() => {
    return commentState?.loading || upperFirst(fields?.status) !== 'Active'
  }, [commentState])

  if (note_id && noteQueryState?.loading)
    return (
      <PreviewNoteDialogBodyContainer className="confirm-dialog-container">
        <Box my="50px">
          <Loader />
        </Box>
      </PreviewNoteDialogBodyContainer>
    )

  return (
    <PreviewNoteDialogBodyContainer className="confirm-dialog-container" $isEvent={Boolean(fields?.appointment)}>
      <NoteCardHeader className="note-card-header">
        <div className="note-card-header--info">
          {fields?.type === 'System Generated' && (
            <div className="note-card-header--user">
              {fields?.is_flagged && 'Member Flagged by System'}
              {!fields?.is_flagged && (
                <Fragment>
                  Member Unflagged by {`${fields?.unflagged_by_user?.full_name}`}
                  <div className="note-card-header--user-type">
                    (
                    {`${upperFirst(
                      fields?.unflagged_by_user?.role_display_name || fields?.unflagged_by_user?.role_name,
                    )}`}
                    )
                  </div>
                </Fragment>
              )}
            </div>
          )}
          {!['System Generated', 'Manual Flag', 'Program/Event Note'].includes(fields.type || '') && (
            <div className="note-card-header--user">
              Note added by {`${fields?.created_by_user?.full_name}`}
              <div className="note-card-header--user-type">
                ({`${upperFirst(fields?.created_by_user?.role_display_name || fields?.created_by_user?.role_name)}`})
              </div>
            </div>
          )}
          {fields?.type === 'Manual Flag' && (
            <div className="note-card-header--user">
              {fields?.is_flagged && (
                <Fragment>
                  Member Flagged by {`${fields?.created_by_user?.full_name}`}
                  <div className="note-card-header--user-type">
                    ({`${upperFirst(fields?.created_by_user?.role_display_name || fields?.created_by_user?.role_name)}`}
                    )
                  </div>
                </Fragment>
              )}
              {!fields?.is_flagged && (
                <Fragment>
                  Member Unflagged by {`${fields?.unflagged_by_user?.full_name}`}
                  <div className="note-card-header--user-type">
                    (
                    {`${upperFirst(
                      fields?.unflagged_by_user?.role_display_name || fields?.unflagged_by_user?.role_name,
                    )}`}
                    )
                  </div>
                </Fragment>
              )}
            </div>
          )}
          {fields?.type === 'Program/Event Note' && (
            <div className="note-card-header--user">
              Program/Event Note added by {`${fields?.created_by_user?.full_name}`}
              <div className="note-card-header--user-type">
                ({`${upperFirst(fields?.created_by_user?.role_display_name || fields?.created_by_user?.role_name)}`})
              </div>
            </div>
          )}
          <div className="note-card-header--date">{dayjs(fields.created_date).format(`MM/DD/YYYY [at] hh:mm A`)}</div>
        </div>
        {(allowRoleVisibility || (fields?.created_by === user?.id && fields?.type !== 'System Generated')) &&
          !(fields?.type === 'System Generated' && fields?.is_flagged) && (
            <MoreOptions row={fields} actions={getNoteActions()} fill="#727272" width="157px" />
          )}
      </NoteCardHeader>
      {fields?.appointment && (
        <Fragment>
          <div className="event-note-container">
            <div className="event-note">
              <div className="event-note--key">
                <SvgCalendarIcon /> Date
              </div>
              <div className="event-note--value">{dayjs(fields?.appointment?.start_date).format(`MM/DD/YYYY`)}</div>
            </div>
            <div className="event-note">
              <div className="event-note--key">
                <SvgTimeIcon /> Time
              </div>
              <div className="event-note--value">
                {`${dayjs(fields?.appointment?.start_date).format('h:mm A')} - ${dayjs(
                  fields?.appointment?.end_date,
                ).format('h:mm A')}`}
              </div>
            </div>
            <div className="event-note">
              <div className="event-note--key">
                <SvgLocationIcon width="15" height="18" /> Location
              </div>
              <div className="event-note--value">{fields?.appointment?.resources?.join(', ')}</div>
            </div>
            <div className="event-note">
              <div className="event-note--key">
                <SvgCoachIcon /> Coach
              </div>
              <div className="event-note--value">{fields?.appointment?.coaches?.join(', ')}</div>
            </div>
          </div>
          <div className="event-note-status">
            <div className="event-note">
              <div className="event-note--key">Attendance</div>
              <div className="event-note--value">{upperFirst(fields?.detail?.attendance_type)}</div>
            </div>
          </div>
        </Fragment>
      )}
      {fields?.message && <div className="note-content">{fields?.message}</div>}
      {fields?.attachments?.map(each => (
        <div className="note-indicator note-indicator--attachment" onClick={onPreviewAttachment(fields)}>
          1 Image attached
        </div>
      ))}
      {upperFirst(fields?.status) !== 'Archive' &&
        dayjs(fields.updated_date).format('MM/DD/YYYY hh:mm:ss A') !==
          dayjs(fields.created_date).format('MM/DD/YYYY hh:mm:ss A') && (
          <Tooltipped
            tooltipClassName="custom-tooltip"
            label={
              fields?.updated_by_user
                ? `
              Edited by ${fields?.updated_by_user?.full_name} (${fields?.updated_by_user?.role_display_name ||
                    fields?.updated_by_user?.role_name})
              on ${dayjs(fields.updated_date).format('MM/DD/YYYY [at] hh:mm A')}
            `
                : `
              Edited by ${fields?.created_by_user?.full_name} (${fields?.created_by_user?.role_display_name ||
                    fields?.created_by_user?.role_name})
              on ${dayjs(fields.updated_date).format('MM/DD/YYYY [at] hh:mm A')}
            `
            }
            position="bottom"
            setPosition
          >
            <div className="note-indicator note-indicator--edited">
              <SvgPencilIcon /> Edited
            </div>
          </Tooltipped>
        )}

      <CommentSectionContainer mt="32px">
        {Boolean(noteCommentQueryState?.data?.length) && (
          <div className="note-indicator note-indicator--comment">
            {`+${noteCommentQueryState?.data?.length} Comments`}
          </div>
        )}
        {Boolean(noteCommentQueryState?.data?.length) && (
          <div className="comment-container">
            {noteCommentQueryState?.data?.map((comment: CommentTypeWithStatus, index: number) => (
              <div className="comment-card-wrapper" key={`comment-card-wrapper-${index}`}>
                <Avatar imageURL={`${comment?.user_photo_url}`} />
                <div className="comment-card">
                  <NoteCardHeader className="comment-card-header">
                    <div className="comment-card-header--info">
                      {upperCase(comment?.status) === 'ACTIVE' && (
                        <Fragment>
                          <div className="comment-card-header--user">
                            {comment?.user_full_name}
                            <div className="comment-card-header--user-type">
                              ({`${upperFirst(comment?.user_role_display_name || comment?.user_role_name)}`})
                            </div>
                          </div>
                          <div className="comment-card-header--date">
                            {dayjs(comment?.created_date).format(`MM/DD/YYYY [at] hh:mm A`)}
                          </div>
                        </Fragment>
                      )}
                      {upperCase(comment?.status) !== 'ACTIVE' && (
                        <Fragment>
                          <div className="comment-card-header--user">
                            {`Comment Deleted by ${comment?.deleted_comment?.user_full_name}`}
                            <div className="comment-card-header--user-type">
                              (
                              {`${upperFirst(
                                comment?.deleted_comment?.user_role_display_name ||
                                  comment?.deleted_comment?.user_role_name,
                              )}`}
                              )
                            </div>
                          </div>
                          <div className="comment-card-header--date">
                            {dayjs(comment?.deleted_comment?.created_date).format(`MM/DD/YYYY [at] hh:mm A`)}
                          </div>
                        </Fragment>
                      )}
                    </div>
                    {(allowRoleVisibility || comment?.user_id === user?.id) &&
                      upperCase(comment?.status) === 'ACTIVE' && (
                        <MoreOptions row={comment} actions={getNoteCommentActions()} fill="#727272" width="157px" />
                      )}
                  </NoteCardHeader>
                  <div className="comment-content">
                    {upperCase(comment?.status) === 'ACTIVE'
                      ? comment?.message
                      : `Reason for deletion: ${comment?.deleted_comment?.message}`}
                    {upperCase(comment?.status) === 'ACTIVE' &&
                      dayjs(comment.updated_date).format('MM/DD/YYYY hh:mm:ss A') !==
                        dayjs(comment.created_date).format('MM/DD/YYYY hh:mm:ss A') && (
                        <span className="comment-edited-indicator">(edited)</span>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CommentSectionContainer>
      <CommentInputContainer mt="24px">
        <Avatar imageURL={`${user?.photo_url}`} />
        <StyledTextField
          id="message"
          placeholder={
            upperFirst(fields?.status) === 'Active' ? 'Write your message here' : 'Comments disabled for archived notes'
          }
          disabled={shouldDisableCommentField}
          maxRows={2}
          rows={1}
          value={commentFormState?.fields?.message}
          onChange={commentFormHandlers?.onElementChange}
          onKeyDown={(event?: React.KeyboardEvent<HTMLElement>) => {
            if (event?.ctrlKey && [10, 13].includes(event?.keyCode)) commentFormHandlers.onValidate()
          }}
          error={Boolean(commentFormState?.errors.message)}
          errorText={commentFormState?.errors.message}
        />
        <StyledButton
          disabled={shouldDisableCommentField}
          variant="primary"
          onClick={() => {
            commentFormHandlers.onValidate()
          }}
          icon={<SvgSendIcon fill={upperFirst(fields?.status) === 'Active' ? '#fff' : '#949494'} />}
        />
      </CommentInputContainer>
      <div className="dialog-button-close" onClick={onClose}>
        Cancel
      </div>
    </PreviewNoteDialogBodyContainer>
  )

  async function onSubmitComment(comment: CommentType) {
    onCommentMutation({
      data: comment,
      onSuccess() {
        commentFormHandlers?.onReset?.()
        noteCommentHandler?.refetch?.()
        refetchPageTable?.()
      },
    })
  }

  function onFetchSuccess(note: NoteType) {
    onSetForm(note)
  }

  function getNoteCommentActions() {
    return [
      {
        label: 'Edit Comment',
        onClick: (data: CommentTypeWithStatus) => onEditComment(data),
        conditionalRendering: (data: CommentTypeWithStatus) => data?.status === 'Active' && data?.user_id === user?.id,
      },
      {
        label: 'Delete Comment',
        color: theme?.colors?.caution,
        onClick: (data: CommentTypeWithStatus) => onDeleteComment(data),
        conditionalRendering: (data: CommentTypeWithStatus) => allowRoleVisibility || data?.user_id === user?.id,
      },
    ]
  }

  function getNoteActions() {
    return [
      {
        label: 'Edit Note',
        onClick: (data: NoteTypeWithStatus) => onEdit(data),
        conditionalRendering: (data: NoteTypeWithStatus) => data?.status === 'Active' && data?.created_by === user?.id,
      },
      {
        label: 'Restore Note',
        color: '#188C46',
        onClick: (data: NoteTypeWithStatus) => onRestore(data),
        conditionalRendering: (data: NoteTypeWithStatus) => data?.status !== 'Active' && allowRoleVisibility,
      },
      {
        label: 'Archive Note',
        color: theme?.colors?.caution,
        onClick: (data: NoteTypeWithStatus) => onArchive(data),
        conditionalRendering: (data: NoteTypeWithStatus) =>
          data?.status === 'Active' && (allowRoleVisibility || data?.created_by === user?.id) && !data?.is_flagged,
      },
      {
        label: 'Delete Note',
        color: theme?.colors?.caution,
        onClick: (data: NoteTypeWithStatus) => onDelete(data),
        conditionalRendering: (data: NoteTypeWithStatus) =>
          (allowRoleVisibility || data?.created_by === user?.id) && !data?.is_flagged,
      },
    ]
  }
}

const WrappedPreviewNoteDialog = flowRight(withDialog())(PreviewNoteDialog)

const StyledTextField = styled(TextField)<{ id?: string }>`
  width: 100%;

  ${({ id }) => `
    textarea#${id} {
      padding: 16px 54px 16px 16px;
      min-height: 60px;
    }
  `}
`

const StyledButton = styled(Button)`
  // background: ${({ theme }) => theme?.colors?.main};
  width: 40px;
  height: 40px;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`

const CommentSectionContainer = styled(Box)`
  .comment-edited-indicator {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #727272;
    display: inline-block;
    margin-left: 4px;
  }

  .comment-container {
    margin-top: 1rem;
    max-height: 450px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 8px;
      background-color: #f5f5f5;
      border-radius: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme?.colors?.label};
      border-radius: 50px;
    }
  }

  .comment-card-wrapper {
    display: flex;
    flex-direction: row;

    &:not(:first-child) {
      margin-top: 16px;
    }
  }

  .avatar-container {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;

    margin-right: 8px;
  }

  .comment-content {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 21px;
    color: ${({ theme }) => theme?.colors?.secondary};

    margin-top: 1.5rem;
    word-wrap: break-word;
    max-width: 420px;
  }

  .comment-card {
    padding: 24px 16px;
    min-height: 50px;
    width: 100%;

    // background: #F9F3F8;
    background: ${({ theme }) => lighten(0.6, theme?.colors?.main)};
    border-radius: 4px;
  }

  .comment-card-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .more-options-item-container {
      top: 24px;
    }

    button {
      height: 0;
      padding-bottom: 24px;
    }

    &--info {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
    }

    &--user {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    &--user-type {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: #727272;
      display: inline-block;
      margin-left: 4px;
    }

    &--date {
      font-weight: 500;
      font-size: 12px;
      line-height: 18px;
      color: #727272;
    }
  }
`

const CommentInputContainer = styled(Box)`
  margin-top: 40px;
  position: relative;
  display: flex;
  align-items: flex-start;

  .avatar-container {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;

    margin-right: 16px;
    margin-top: 8px;
  }
`

const PreviewNoteDialogBodyContainer = styled(Box)<{ $isEvent?: boolean }>`
  color: ${({ theme }) => theme.colors.secondary};
  padding: 40px;
  max-width: 632px;
  min-width: ${({ $isEvent }) => ($isEvent ? '632px' : '540px')};
  .event-note {
    display: flex;
    flex-direction: row;
    align-items: center;

    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme?.colors?.secondary};

    &--key {
      font-weight: bold;
      display: flex;
      align-items: center;

      min-width: 95px;
      margin-right: 24px;

      svg {
        margin-right: 8px;
      }
    }

    &--value {
      font-weight: 500;
    }

    &-status {
      margin-top: 1.5rem;
    }

    &-container {
      display: grid;
      grid-template-rows: auto auto;
      grid-template-columns: repeat(auto-fit, minmax(272px, 1fr));
      row-gap: 0.5rem;

      margin-top: 1.5rem;
    }
  }

  .dialog-button-close {
    font-weight: bold;
    font-size: 18px;
    line-height: 24px;

    color: ${({ theme }) => theme?.colors?.main};
    text-decoration-line: underline;
    text-align: center;
    cursor: pointer;

    width: fit-content;
    margin: 40px auto 0;
  }

  .note-content {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 21px;
    color: ${({ theme }) => theme?.colors?.secondary};
    white-space: pre-wrap;
    margin-top: 1.5rem;
  }

  .note-indicator {
    color: ${({ theme }) => theme?.colors?.main};
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    &--attachment {
      text-decoration-line: underline;
      margin-top: 1.5rem;
      width: fit-content;
    }

    &--new-comment {
      color: #188c46;
      background: #d9f2e9;
      width: fit-content;
      border-radius: 4px;
      margin-bottom: 10px;
      padding: 0.25rem 1rem;
      cursor: unset;
    }

    &--comment {
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    &--edited {
      display: flex;
      align-items: center;
      color: ${({ theme }) => theme?.colors?.secondary};
      margin-top: 1.2rem;
      width: fit-content;

      svg {
        margin-right: 4px;
      }

      .md-tooltip--bottom-active {
        transform: translate3d(-12%, 100%, 0);

        &::after {
          left: 20px;
          transform: translateY(-10px);
        }
      }
    }
  }
`

WrappedPreviewNoteDialog.defaultProps = {
  // continueIsDisabled(data: NoteType) {
  //   return !data?.message?.length
  // },
}

export default WrappedPreviewNoteDialog
